/** array of month names */
import {strtotime} from '@/library/strtotime';
import dateHelper, {parseDate} from '../utils/portuna/dateHelper';
import {getNumericValue} from './math';

const minutesPerDay = 1440;

const secondsPerDay = 86400;

const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

/** array of short month names */
const monthNamesShort = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const monthNamesEn = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const monthNamesEnLowerCase = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/** array of day names */
const dayNames = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

/** array of day names */
const dayNamesEn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** array of day names lowercase */
const dayNamesEnLowerCase: TDayNames[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/** array of short day names */
const dayNamesShort = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** array of each hour of a day */
const dayHours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];

/** week days with keys */
const weekDays: { name: string, key: TDayNames}[] = [
    {
        name: 'Montag',
        key: 'monday',
    },
    {
        name: 'Dienstag',
        key: 'tuesday',
    },
    {
        name: 'Mittwoch',
        key: 'wednesday',
    },
    {
        name: 'Donnerstag',
        key: 'thursday',
    },
    {
        name: 'Freitag',
        key: 'friday',
    },
    {
        name: 'Samstag',
        key: 'saturday',
    },
    {
        name: 'Sonntag',
        key: 'sunday',
    },
];

const weekDaysMap: Record<TDayNames, string> = weekDays.reduce((carry, day) => Object.assign(carry, {[day.key]: day.name}), {});

const completeDayInfo = [
    {
        short: dayNamesShort[1],
        long: dayNames[1],
        longEn: dayNamesEn[1],
        longEnLowerCase: dayNamesEnLowerCase[1],
    },
    {
        short: dayNamesShort[2],
        long: dayNames[2],
        longEn: dayNamesEn[2],
        longEnLowerCase: dayNamesEnLowerCase[2],
    },
    {
        short: dayNamesShort[3],
        long: dayNames[3],
        longEn: dayNamesEn[3],
        longEnLowerCase: dayNamesEnLowerCase[3],
    },
    {
        short: dayNamesShort[4],
        long: dayNames[4],
        longEn: dayNamesEn[4],
        longEnLowerCase: dayNamesEnLowerCase[4],
    },
    {
        short: dayNamesShort[5],
        long: dayNames[5],
        longEn: dayNamesEn[5],
        longEnLowerCase: dayNamesEnLowerCase[5],
    },
    {
        short: dayNamesShort[6],
        long: dayNames[6],
        longEn: dayNamesEn[6],
        longEnLowerCase: dayNamesEnLowerCase[6],
    },
    {
        short: dayNamesShort[0],
        long: dayNames[0],
        longEn: dayNamesEn[0],
        longEnLowerCase: dayNamesEnLowerCase[0],
    },
];

const completeMonthInfo = [
    {
        short: monthNamesShort[0],
        long: monthNames[0],
        longEn: monthNamesEn[0],
        longEnLowerCase: monthNamesEnLowerCase[0],
    },
    {
        short: monthNamesShort[1],
        long: monthNames[1],
        longEn: monthNamesEn[1],
        longEnLowerCase: monthNamesEnLowerCase[1],
    },
    {
        short: monthNamesShort[2],
        long: monthNames[2],
        longEn: monthNamesEn[2],
        longEnLowerCase: monthNamesEnLowerCase[2],
    },
    {
        short: monthNamesShort[3],
        long: monthNames[3],
        longEn: monthNamesEn[3],
        longEnLowerCase: monthNamesEnLowerCase[3],
    },
    {
        short: monthNamesShort[4],
        long: monthNames[4],
        longEn: monthNamesEn[4],
        longEnLowerCase: monthNamesEnLowerCase[4],
    },
    {
        short: monthNamesShort[5],
        long: monthNames[5],
        longEn: monthNamesEn[5],
        longEnLowerCase: monthNamesEnLowerCase[5],
    },
    {
        short: monthNamesShort[6],
        long: monthNames[6],
        longEn: monthNamesEn[6],
        longEnLowerCase: monthNamesEnLowerCase[6],
    },
    {
        short: monthNamesShort[7],
        long: monthNames[7],
        longEn: monthNamesEn[7],
        longEnLowerCase: monthNamesEnLowerCase[7],
    },
    {
        short: monthNamesShort[8],
        long: monthNames[8],
        longEn: monthNamesEn[8],
        longEnLowerCase: monthNamesEnLowerCase[8],
    },
    {
        short: monthNamesShort[9],
        long: monthNames[9],
        longEn: monthNamesEn[9],
        longEnLowerCase: monthNamesEnLowerCase[9],
    },
    {
        short: monthNamesShort[10],
        long: monthNames[10],
        longEn: monthNamesEn[10],
        longEnLowerCase: monthNamesEnLowerCase[10],
    },
    {
        short: monthNamesShort[11],
        long: monthNames[11],
        longEn: monthNamesEn[11],
        longEnLowerCase: monthNamesEnLowerCase[11],
    },
];

function getDateInformation(date: string | number | Date = new Date()): DateInformation
{
    let _date;

    if (date instanceof Date)
    {
        _date = date;
    }
    else if ('number' === typeof getNumericValue(date))
    {
        _date = parseDate(date as number * 1000);
    }
    else
    {
        _date = parseDate(date);
    }

    if (!(_date instanceof Date))
    {
        _date = new Date();
    }

    const month = _date.getMonth();
    const day   = _date.getDay();

    const monday = new Date(_date.getFullYear(), month, _date.getDate() - ((day || 7) - 1));

    return {
        dayName: dayNames[day],
        dayNameEn: dayNamesEn[day],
        dayNameEnLowerCase: dayNamesEnLowerCase[day],
        dayNameShort: dayNamesShort[day],
        monthName: monthNames[month],
        monthNameShort: monthNamesShort[month],
        date: dateHelper('Y-m-d', _date) as string,
        dateGerman: dateHelper('d.m.Y', _date) as string,
        day: _date.getDate(),
        dayString: ('0' + _date.getDate()).slice(-2),
        weekday: dateHelper('N', _date) as number,
        month: month + 1,
        monthString: ('0' + (month + 1)).slice(-2),
        year: _date.getFullYear(),
        week: dateHelper('W', _date) as string,
        monday: dateHelper('Y-m-d', monday) as string,
        mondayFormatted: dateHelper('d.m.Y', monday) as string,
    };
}

function parseDateRange(start: string, end: Nullable<string>, forScreenReader = false): string
{
    if (end && start !== end)
    {
        const format    = forScreenReader ? 'j/n/Y' : 'd/m/Y';
        const delimiter = forScreenReader ? 'bis' : '-';

        const [startDay, startMonth, startYear] = (dateHelper(format, start) as string).split('/');
        const [endDay, endMonth, endYear]       = (dateHelper(format, end) as string).split('/');

        if (startYear !== endYear)
        {
            return `${startDay}.${startMonth}.${startYear} ${delimiter} ${endDay}.${endMonth}.${endYear}`;
        }
        else if (startMonth !== endMonth)
        {
            return `${startDay}.${startMonth}. ${delimiter} ${endDay}.${endMonth}.${endYear}`;
        }
        else
        {
            return `${startDay}. ${delimiter} ${endDay}.${endMonth}.${endYear}`;
        }
    }
    else
    {
        let information = getDateInformation(start);
        return `${information.dayNameShort},  ${information.dayString}.${information.monthString}.${information.year}`;
    }
}

function setToMonday(date: Date)
{
    date.setDate(date.getDate() - (date.getDay() || 7) + 1);
    return date;
}

function incrementDate(date: Date, add: number = 1)
{
    date.setDate(date.getDate() + add);

    return date;
}

function incrementHours(date: Date, add: number = 1)
{
    date.setHours(date.getHours() + add);

    return date;
}

function getDayOfWeek(relationDate: string, day: TDayNames)
{
    const increment = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].indexOf(day);
    const date   = new Date(relationDate);

    if (increment === -1)
    {
        console.error('invalid day');
        return relationDate;
    }

    setToMonday(date);
    incrementDate(date, increment);

    return dateHelper('Y-m-d', date) as string;
}

function getMonday(relationDate: Nullable<string> = null)
{
    const date = relationDate ? new Date(relationDate) : new Date();

    setToMonday(date);

    return dateHelper('Y-m-d', date) as string;
}

function getDayName(date: string): TDayNames
{
    let dayIndex = date && parseInt(<string>dateHelper('N', date)) || 1;

    return weekDays[dayIndex - 1].key;
}

function getDateByIsoWeek(year: number, week: number)
{
    return new Date(strtotime(year + 'W' + (week < 10 ? 0 : '') + week) * 1000);
}

function getAge(date: string, relationDate: Nullable<string> = null): number
{
    if (!date || relationDate && relationDate <= date)
    {
        return 0;
    }

    const birthDate = new Date(date);
    const today     = relationDate ? new Date(relationDate) : new Date();

    let age: number         = today.getFullYear() - birthDate.getFullYear();
    const monthDiff: number = today.getMonth() - birthDate.getMonth();
    const dayDiff: number   = today.getDate() - birthDate.getDate();

    if (monthDiff < 0 || monthDiff === 0 && dayDiff < 0)
    {
        age--;
    }

    return age;
}

enum Day {
    Sunday,
    Monday,
    Tuesday,
    Wednesday,
    Thursday,
    Friday,
    Saturday
}

function isAllDay(startTime: Nullable<string>, endTime: Nullable<string>): boolean
{
    return ['', '00:00'].includes((startTime || '').substring(0, 5))
        && ['', '00:00', '24:00', '23:59'].includes((endTime || '').substring(0, 5));
}

export {
    getDateInformation,
    parseDateRange,
    setToMonday,
    getMonday,
    getDayOfWeek,
    incrementDate,
    incrementHours,
    getDateByIsoWeek,
    getAge,
    isAllDay,
    getDayName,
    monthNames,
    monthNamesEn,
    monthNamesShort,
    completeMonthInfo,
    dayNames,
    dayNamesEn,
    dayNamesEnLowerCase,
    dayNamesShort,
    completeDayInfo,
    weekDays,
    weekDaysMap,
    minutesPerDay,
    secondsPerDay,
    dayHours,
    Day
};